class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.initialResultsMarkup = this.predictiveSearchResults.innerHTML;
    this.statusElement = this.querySelector('.predictive-search-status');
    this.searchTerm = '';
    this.renderedSearchTerm = '';
    this.requestId = 0;
    this.isOpen = false;
    this.instanceId = `search-${++PredictiveSearch.instanceCount}`;
    this.resultsId = `predictive-search-results-${this.instanceId}`;
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);
    this.onViewportChange = this.updateResultsHeight.bind(this);

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
  }

  connectedCallback() {
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('resize', this.onViewportChange);
    window.visualViewport?.addEventListener('scroll', this.onViewportChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cancelPendingRequest();
    clearTimeout(this.statusTimeout);
    clearTimeout(this.focusOutTimeout);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange);
    window.visualViewport?.removeEventListener('resize', this.onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
  }

  onSearchInput(event) {
    // Invalidate immediately, before debounce or a cache hit can let an old response win.
    this.close();
    this.searchTerm = this.getQuery();
    this.renderedSearchTerm = '';
    this.removeAttribute('results');
    super.onSearchInput(event);
  }

  onChange() {
    super.onChange();
    this.searchTerm = this.getQuery();
    if (!this.searchTerm || this.isComposing) {
      this.close();
      if (!this.isComposing && this.contains(document.activeElement)) this.renderDirectoryOnly('');
      return;
    }
    if (this.contains(document.activeElement)) this.getSearchResults(this.searchTerm);
  }

  onFormReset(event) {
    this.suppressStarters = true;
    this.close(true);
    super.onFormReset(event);
    this.suppressStarters = false;
  }

  onFocus() {
    clearTimeout(this.focusOutTimeout);
    const query = this.getQuery();
    if (!query) {
      if (!this.suppressStarters) this.renderDirectoryOnly('');
      return;
    }
    if (this.hasAttribute('results') && this.renderedSearchTerm === query) {
      this.open();
    } else {
      clearTimeout(this.inputTimeout);
      this.searchTerm = query;
      this.getSearchResults(query);
    }
  }

  onFocusOut() {
    clearTimeout(this.focusOutTimeout);
    this.focusOutTimeout = setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 0);
  }

  onKeydown(event) {
    if (event.isComposing || this.isComposing || event.keyCode === 229) return;
    const key = event.key || event.code;

    if (key === 'Escape' && (this.isOpen || this.hasAttribute('loading'))) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }

    if (!this.isOpen || this.hasAttribute('loading') || this.renderedSearchTerm !== this.getQuery()) return;

    if (key === 'ArrowUp' || key === 'ArrowDown') {
      event.preventDefault();
      this.switchOption(key === 'ArrowUp' ? 'up' : 'down');
    } else if (key === 'Enter') {
      const selected = this.predictiveSearchResults.querySelector('[role="option"][aria-selected="true"]');
      const target = selected?.matches('a, button') ? selected : selected?.querySelector('a, button');
      if (target) {
        event.preventDefault();
        target.click();
      }
    }
  }

  switchOption(direction) {
    const options = Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]')).filter(
      (option) => option.offsetParent !== null
    );
    if (!options.length) return;
    const selectedIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    const nextIndex = direction === 'up'
      ? (selectedIndex <= 0 ? options.length - 1 : selectedIndex - 1)
      : (selectedIndex + 1) % options.length;

    options.forEach((option, index) => option.setAttribute('aria-selected', String(index === nextIndex)));
    this.input.setAttribute('aria-activedescendant', options[nextIndex].id);
    options[nextIndex].scrollIntoView({ block: 'nearest' });
  }

  cancelPendingRequest() {
    this.abortController?.abort();
    this.abortController = null;
    this.requestId += 1;
  }

  async getSearchResults(searchTerm) {
    this.cancelPendingRequest();
    const requestId = this.requestId;
    const queryKey = JSON.stringify([this.input.form.action, searchTerm]);
    this.setLiveRegionLoadingState();

    // Abort an earlier request even when the replacement is served from cache.
    if (PredictiveSearch.cachedResults.has(queryKey)) {
      const markup = PredictiveSearch.cachedResults.get(queryKey);
      PredictiveSearch.cachedResults.delete(queryKey);
      PredictiveSearch.cachedResults.set(queryKey, markup);
      this.renderSearchResults(markup, searchTerm);
      return;
    }

    const controller = new AbortController();
    this.abortController = controller;
    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,query',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
    });

    try {
      const response = await fetch(`${window.routes.predictive_search_url}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
      const text = await response.text();
      if (controller.signal.aborted || requestId !== this.requestId || this.getQuery() !== searchTerm) return;

      const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
      if (!section?.querySelector('#predictive-search-results')) throw new Error('Missing predictive search results.');

      PredictiveSearch.cachedResults.set(queryKey, section.innerHTML);
      if (PredictiveSearch.cachedResults.size > 50) {
        PredictiveSearch.cachedResults.delete(PredictiveSearch.cachedResults.keys().next().value);
      }
      this.renderSearchResults(section.innerHTML, searchTerm);
    } catch (error) {
      if (controller.signal.aborted || requestId !== this.requestId) return;
      if (!this.renderDirectoryOnly(searchTerm, true)) {
        this.close();
        this.removeAttribute('results');
        this.renderedSearchTerm = '';
      }
    } finally {
      if (this.abortController === controller) this.abortController = null;
    }
  }

  setLiveRegionLoadingState() {
    this.predictiveSearchResults.innerHTML = this.initialResultsMarkup;
    const directory = this.createDirectoryListbox();
    if (directory) this.predictiveSearchResults.prepend(directory);
    this.setAttribute('loading', '');
    this.input.setAttribute('aria-busy', 'true');
    this.setLiveRegionText(this.getAttribute('data-loading-text'));
    this.open();
  }

  createDirectoryListbox(failed = false) {
    const helper = window.WellstrapsSearch;
    if (!helper?.enabled()) return null;
    const context = helper.resolve(this.input.value);
    const panel = helper.createPanel(context, this.instanceId, { failed });
    if (!panel) return null;
    const listbox = document.createElement('div');
    listbox.id = this.resultsId;
    listbox.setAttribute('role', 'listbox');
    listbox.setAttribute('aria-label', helper.config().text?.navigation || this.input.getAttribute('placeholder') || 'Search');
    listbox.append(panel);
    return listbox;
  }

  renderDirectoryOnly(query, failed = false) {
    const listbox = this.createDirectoryListbox(failed);
    if (!listbox || !this.contains(document.activeElement)) return false;
    this.predictiveSearchResults.replaceChildren(listbox);
    this.renderedSearchTerm = query;
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    this.input.removeAttribute('aria-busy');
    this.setLiveRegionText(listbox.getAttribute('aria-label'));
    this.open();
    return true;
  }

  setLiveRegionText(text) {
    if (!this.statusElement) return;
    clearTimeout(this.statusTimeout);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text || '';
    this.statusTimeout = setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(markup, searchTerm) {
    if (!this.isConnected || !this.contains(document.activeElement) || this.getQuery() !== searchTerm) {
      this.close();
      return;
    }
    this.predictiveSearchResults.innerHTML = markup;
    window.WellstrapsSearch?.enhance(this.predictiveSearchResults, window.WellstrapsSearch.resolve(this.input.value), this.instanceId);

    // Each search field needs its own listbox and option IDs, including cached HTML.
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
      const originalId = element.id;
      element.id = `${originalId}-${this.instanceId}`;
      ids.set(originalId, element.id);
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby], [aria-controls]').forEach((element) => {
      ['aria-labelledby', 'aria-controls'].forEach((attribute) => {
        if (!element.hasAttribute(attribute)) return;
        element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map((id) => ids.get(id) || id).join(' '));
      });
    });

    this.predictiveSearchResults.querySelectorAll('.predictive-search__item-query-result').forEach((result) => {
      const link = result.closest('a');
      if (!link) return;
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('type', 'product');
      link.href = `${url.pathname}${url.search}`;
    });

    this.renderedSearchTerm = searchTerm;
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    this.input.removeAttribute('aria-busy');
    this.input.removeAttribute('aria-activedescendant');
    this.setLiveRegionText(this.predictiveSearchResults.querySelector('[data-predictive-search-live-region-count-value]')?.textContent);
    this.open();
  }

  updateResultsHeight() {
    if (!this.isOpen) return;
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
    const field = this.input.closest('.field') || this.input;
    const rect = field.getBoundingClientRect();
    const below = viewportBottom - rect.bottom - 12;
    const above = rect.top - viewportTop - 12;
    const placeAbove = below < 180 && above > below;
    this.toggleAttribute('results-above', placeAbove);
    this.predictiveSearchResults.style.maxHeight = `${Math.max(0, Math.min(560, placeAbove ? above : below))}px`;
  }

  open() {
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.updateResultsHeight();
  }

  close(clearSearchTerm = false) {
    clearTimeout(this.inputTimeout);
    clearTimeout(this.statusTimeout);
    this.cancelPendingRequest();
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.renderedSearchTerm = '';
      this.removeAttribute('results');
    }
    this.predictiveSearchResults.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
    this.input.removeAttribute('aria-activedescendant');
    this.input.removeAttribute('aria-busy');
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.removeAttribute('results-above');
    this.predictiveSearchResults.style.removeProperty('max-height');
    if (this.statusElement) {
      this.statusElement.textContent = '';
      this.statusElement.setAttribute('aria-hidden', 'true');
    }
    this.isOpen = false;
  }
}

PredictiveSearch.cachedResults = new Map();
PredictiveSearch.instanceCount = 0;
customElements.define('predictive-search', PredictiveSearch);
